import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, CheckCircle } from 'lucide-react';
import { Button, Input } from '../ui';
import { validateEmail } from '../../utils/validation';
import { useToast } from '../../hooks';

const NewsletterForm = ({ className = '' }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const { showSuccess, showError } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setIsSuccess(true);
      setEmail('');
      showSuccess(
        'Successfully subscribed!',
        'Welcome to the Pragyavan community. You\'ll receive wellness tips and updates.'
      );
    } catch (err) {
      setError('Something went wrong. Please try again.');
      showError(
        'Subscription failed',
        'There was an error subscribing to our newsletter. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError('');
    if (isSuccess) setIsSuccess(false);
  };

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className={`text-center ${className}`}
      >
        <div className="inline-flex items-center justify-center w-16 h-16 bg-sage-green rounded-full mb-4">
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-lg font-semibold text-pragyavan-brown mb-2">
          Thank you for subscribing!
        </h3>
        <p className="text-warm-gray text-sm mb-4">
          You'll receive wellness tips and updates from Pragyavan.
        </p>
        <Button
          variant="ghost"
          size="small"
          onClick={() => setIsSuccess(false)}
        >
          Subscribe Another Email
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={className}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={handleEmailChange}
              disabled={isSubmitting}
              error={error}
              icon={Mail}
              className="w-full"
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            size="medium"
            loading={isSubmitting}
            disabled={isSubmitting}
            className="whitespace-nowrap"
          >
            Subscribe
          </Button>
        </div>
        
        <p className="text-xs text-warm-gray/70 text-center sm:text-left">
          We respect your privacy. Unsubscribe at any time.
        </p>
      </form>
    </motion.div>
  );
};

export default NewsletterForm;